'use client';

import { XClose } from '@untitledui/icons';
import type { RefAttributes } from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

interface TagCloseXProps
  extends AriaButtonProps, RefAttributes<HTMLButtonElement> {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const styles = {
  sm: { root: 'p-0.5', icon: 'size-2.5' },
  md: { root: 'p-0.5', icon: 'size-3' },
  lg: { root: 'p-0.75', icon: 'size-3.5' },
};

/**
 * Close button for tags and removable chips.
 *
 * When used inside React Aria's Tag component, pass `slot='remove'` to enable
 * the built-in tag removal behavior. When used standalone (e.g., in email chips),
 * omit the slot prop to avoid slot validation errors in Dialog contexts.
 */
export const TagCloseX = ({
  size = 'md',
  className,
  slot,
  ...otherProps
}: TagCloseXProps) => {
  return (
    <AriaButton
      slot={slot}
      aria-label='Remove this tag'
      className={cx(
        'flex cursor-pointer rounded-[3px] text-fg-quaternary outline-transparent transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed',
        styles[size].root,
        className
      )}
      {...otherProps}
    >
      <XClose
        className={cx('transition-inherit-all', styles[size].icon)}
        strokeWidth='3'
      />
    </AriaButton>
  );
};
